import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatNumber } from '../../utils/formatters';
import ShiftEditor from './ShiftEditor';
import {
  getHrSchedule, saveHrShift, deleteHrShift, copyHrScheduleWeek, scheduleToPayroll,
} from '../../services/api';

/* Расписание на неделю: строка — сотрудник, колонка — день. Это ПЛАН, а не
   факт: в табель он попадает только по кнопке «Перенести в табель», и уже
   заполненные дни при этом не перетираются. */

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

const toISO = (date) => {
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 10);
};

const shiftWeek = (weekStart, deltaDays) => {
  const date = new Date(`${weekStart}T00:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return toISO(date);
};

const currentWeekStart = () => {
  const today = new Date();
  const weekday = (today.getDay() + 6) % 7; // понедельник = 0
  today.setDate(today.getDate() - weekday);
  return toISO(today);
};

const dayLabel = (iso) => {
  const date = new Date(`${iso}T00:00:00`);
  return `${date.getDate()} ${MONTHS_GEN[date.getMonth()]}`;
};

const weekLabel = (dates) => {
  if (!dates.length) return '';
  const from = new Date(`${dates[0]}T00:00:00`);
  const to = new Date(`${dates[6]}T00:00:00`);
  const fromText = from.getMonth() === to.getMonth()
    ? `${from.getDate()}`
    : `${from.getDate()} ${MONTHS_GEN[from.getMonth()]}`;
  return `${fromText} – ${to.getDate()} ${MONTHS_GEN[to.getMonth()]} ${to.getFullYear()}`;
};

const ScheduleTab = ({ employees }) => {
  const [weekStart, setWeekStart] = useState(currentWeekStart);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyWithShifts, setOnlyWithShifts] = useState(false);
  const [showFacts, setShowFacts] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (week) => {
    setLoading(true);
    try {
      const result = await getHrSchedule(week);
      setData(result);
    } catch (e) {
      setNotice({ type: 'error', text: e.response?.data?.error || 'Не удалось загрузить расписание' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(weekStart); }, [weekStart, load]);

  const dates = data?.dates || [];

  // shifts[employee_id][date] — сетка рисуется по этой карте
  const shiftMap = useMemo(() => {
    const map = {};
    (data?.shifts || []).forEach(shift => {
      if (!map[shift.employee_id]) map[shift.employee_id] = {};
      map[shift.employee_id][shift.date] = shift;
    });
    return map;
  }, [data]);

  // facts[employee_id][date] — то, что реально попало в табель за этот день
  const factMap = useMemo(() => {
    const map = {};
    (data?.facts || []).forEach(fact => {
      if (!map[fact.employee_id]) map[fact.employee_id] = {};
      map[fact.employee_id][fact.date] = fact;
    });
    return map;
  }, [data]);

  /* Сопоставление плана и факта. Отсутствие факта — это НЕ прогул: табель
     могли просто ещё не заполнить, поэтому такой случай нейтральный, а
     тревожным считается только реальное расхождение. */
  const compare = (shift, fact) => {
    if (!shift && !fact) return null;
    if (shift && shift.kind !== 'shift') {
      return fact && fact.hours > 0
        ? { state: 'conflict', text: `вышел ${fact.hours} ч` }
        : { state: 'ok', text: shift.kind_label.toLowerCase() };
    }
    if (!shift && fact) {
      if (fact.day_type !== 'work') return { state: 'neutral', text: 'вне графика' };
      return { state: 'extra', text: `+${fact.hours} ч вне графика` };
    }
    if (shift && !fact) return { state: 'neutral', text: 'нет факта' };
    if (fact.day_type !== 'work') return { state: 'conflict', text: 'отсутствие' };
    const diff = Math.round((fact.hours - shift.hours) * 10) / 10;
    if (Math.abs(diff) < 1) return { state: 'ok', text: `${fact.hours} ч` };
    return { state: diff > 0 ? 'over' : 'under', text: `${fact.hours} ч (${diff > 0 ? '+' : ''}${diff})` };
  };

  const visible = useMemo(() => employees.filter(employee => {
    if (employee.status !== 'active') return false;
    if (onlyWithShifts && !shiftMap[employee.id]) return false;
    if (search.trim() && !employee.full_name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  }), [employees, shiftMap, onlyWithShifts, search]);

  const employeeHours = (employeeId) =>
    Object.values(shiftMap[employeeId] || {}).reduce((sum, shift) => sum + shift.hours, 0);

  const dayStats = useMemo(() => {
    const stats = {};
    dates.forEach(date => { stats[date] = { people: 0, hours: 0 }; });
    visible.forEach(employee => {
      dates.forEach(date => {
        const shift = shiftMap[employee.id]?.[date];
        // Отпускник в этот день в смене не стоит — в «В смене» он не считается.
        if (shift && shift.kind === 'shift') {
          stats[date].people += 1;
          stats[date].hours += shift.hours;
        }
      });
    });
    return stats;
  }, [dates, visible, shiftMap]);

  const weekHours = visible.reduce((sum, employee) => sum + employeeHours(employee.id), 0);
  const cellsOf = (employeeId) => Object.values(shiftMap[employeeId] || {});
  const weekShifts = visible.reduce(
    (sum, employee) => sum + cellsOf(employee.id).filter(cell => cell.kind === 'shift').length, 0
  );
  const weekAbsences = visible.reduce(
    (sum, employee) => sum + cellsOf(employee.id).filter(cell => cell.kind !== 'shift').length, 0
  );
  const weekFactHours = visible.reduce(
    (sum, employee) => sum + Object.values(factMap[employee.id] || {})
      .reduce((inner, fact) => inner + (fact.hours || 0), 0), 0
  );

  const saveShift = async (payload) => {
    try {
      const result = await saveHrShift({
        employee_id: editing.employee.id,
        date: editing.date,
        ...payload,
      });
      setData(prev => ({
        ...prev,
        shifts: [...prev.shifts.filter(s => s.id !== result.shift.id), result.shift],
      }));
      setEditing(null);
      return null;
    } catch (e) {
      return e.response?.data?.error || 'Не удалось сохранить смену';
    }
  };

  const removeShift = async () => {
    try {
      await deleteHrShift(editing.shift.id);
      setData(prev => ({ ...prev, shifts: prev.shifts.filter(s => s.id !== editing.shift.id) }));
      setEditing(null);
    } catch (e) {
      setNotice({ type: 'error', text: e.response?.data?.error || 'Не удалось убрать смену' });
    }
  };

  const copyPreviousWeek = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await copyHrScheduleWeek(weekStart, shiftWeek(weekStart, -7));
      setData(prev => ({ ...prev, shifts: result.shifts }));
      setNotice({
        type: result.copied ? 'ok' : 'error',
        text: result.copied
          ? `Перенесено смен: ${result.copied}`
            + (result.skipped ? `, пропущено уже занятых дней: ${result.skipped}` : '')
          : result.source_shifts
            ? 'Все дни этой недели уже заняты — ничего не изменилось'
            : 'На прошлой неделе смен не было',
      });
    } catch (e) {
      setNotice({ type: 'error', text: e.response?.data?.error || 'Не удалось скопировать неделю' });
    } finally {
      setBusy(false);
    }
  };

  const transferToPayroll = async () => {
    if (!window.confirm('Перенести плановые часы этой недели в табель? Уже заполненные дни останутся как есть.')) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await scheduleToPayroll(weekStart);
      setNotice({
        type: 'ok',
        text: `В табель перенесено дней: ${result.filled}`
          + (result.skipped ? `, пропущено уже заполненных: ${result.skipped}` : '')
          + (result.periods.length ? `. Затронуты периоды: ${result.periods.join(', ')}` : '')
          + (result.closed_periods.length ? `. Закрытые периоды пропущены: ${result.closed_periods.join(', ')}` : ''),
      });
    } catch (e) {
      setNotice({ type: 'error', text: e.response?.data?.error || 'Не удалось перенести в табель' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hr-schedule">
      <div className="hr-toolbar">
        <div className="hr-week-nav">
          <button className="ui-icon-btn" onClick={() => setWeekStart(shiftWeek(weekStart, -7))} title="Предыдущая неделя">‹</button>
          <span className="hr-week-label">{weekLabel(dates) || '…'}</span>
          <button className="ui-icon-btn" onClick={() => setWeekStart(shiftWeek(weekStart, 7))} title="Следующая неделя">›</button>
          <button className="ui-btn-ghost" onClick={() => setWeekStart(currentWeekStart())}>Текущая</button>
        </div>
        <div className="hr-toolbar-actions">
          <button className="ui-btn-ghost" onClick={copyPreviousWeek} disabled={busy}>Копировать прошлую</button>
          <button className="ui-btn-ghost" onClick={() => window.print()}>Печать</button>
          <button className="ui-btn-ghost" onClick={transferToPayroll} disabled={busy || weekShifts === 0}>
            Перенести в табель
          </button>
        </div>
      </div>

      <div className="hr-days-controls">
        <input
          className="hr-search"
          placeholder="Поиск по имени…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="hr-checkbox">
          <input type="checkbox" checked={onlyWithShifts} onChange={() => setOnlyWithShifts(v => !v)} />
          <span>Только со сменами</span>
        </label>
        <label className="hr-checkbox">
          <input type="checkbox" checked={showFacts} onChange={() => setShowFacts(v => !v)} />
          <span>Показать факт</span>
        </label>
        <span className="ui-dim">
          {weekShifts} смен · {formatNumber(Math.round(weekHours * 10) / 10)} ч за неделю
          {weekAbsences > 0 && ` · ${weekAbsences} отсутствий`}
          {showFacts && ` · факт ${formatNumber(Math.round(weekFactHours * 10) / 10)} ч`}
        </span>
      </div>

      {notice && (
        <div className={`hr-notice ${notice.type === 'error' ? 'error' : ''}`}>
          <span>{notice.text}</span>
          <button className="hr-notice-close" onClick={() => setNotice(null)}>×</button>
        </div>
      )}

      <div className="hr-print-title">
        Расписание · {weekLabel(dates)}
      </div>

      {loading && !data ? (
        <div className="hr-empty"><div className="hr-empty-title">Загрузка расписания…</div></div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table hr-schedule-table">
            <thead>
              <tr>
                <th className="hr-col-name">Сотрудник</th>
                {dates.map((date, index) => {
                  const weekend = index >= 5;
                  return (
                    <th key={date} className={`hr-shift-th ${weekend ? 'weekend' : ''}`}>
                      <span className="hr-day-num">{WEEKDAYS[index]}</span>
                      <span className="hr-day-weekday">{dayLabel(date)}</span>
                    </th>
                  );
                })}
                <th className="hr-col-sum">Часов</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={9} className="hr-cell-empty">
                    {onlyWithShifts ? 'На этой неделе смен ещё нет' : 'Никто не подходит под фильтры'}
                  </td>
                </tr>
              ) : visible.map(employee => (
                <tr key={employee.id}>
                  <td className="hr-col-name">
                    <span className="hr-row-name">{employee.full_name}</span>
                    <span className="hr-row-position">{employee.position}</span>
                  </td>
                  {dates.map((date, index) => {
                    const shift = shiftMap[employee.id]?.[date];
                    const comparison = showFacts ? compare(shift, factMap[employee.id]?.[date]) : null;
                    return (
                      <td key={date} className={`hr-shift-td ${index >= 5 ? 'weekend' : ''}`}>
                        <button
                          className={`hr-shift ${shift ? `set kind-${shift.kind}` : 'empty'} ${comparison ? 'with-fact' : ''}`}
                          onClick={() => setEditing({ employee, date, shift })}
                          title={shift
                            ? `${shift.kind === 'shift' ? `${shift.hours} ч` : shift.kind_label}${shift.note ? ` · ${shift.note}` : ''}`
                            : 'Поставить смену или отметить отсутствие'}
                        >
                          {!shift ? '+' : shift.kind === 'shift' ? (
                            <>
                              <span className="hr-shift-time">{shift.start_time}–{shift.end_time}</span>
                              <span className="hr-shift-hours">{shift.hours} ч</span>
                            </>
                          ) : (
                            <span className="hr-shift-time">{shift.kind_label}</span>
                          )}
                        </button>
                        {comparison && (
                          <span className={`hr-fact ${comparison.state}`}>{comparison.text}</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="hr-col-sum hr-num-strong">
                    {employeeHours(employee.id) ? `${formatNumber(employeeHours(employee.id))} ч` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            {visible.length > 0 && (
              <tfoot>
                <tr>
                  <td className="hr-col-name">В смене</td>
                  {dates.map((date, index) => (
                    <td key={date} className={`hr-shift-td ${index >= 5 ? 'weekend' : ''}`}>
                      <span className="hr-day-static">
                        {dayStats[date]?.people
                          ? `${dayStats[date].people} чел. · ${formatNumber(dayStats[date].hours)} ч`
                          : '—'}
                      </span>
                    </td>
                  ))}
                  <td className="hr-col-sum hr-num-strong">{formatNumber(Math.round(weekHours * 10) / 10)} ч</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {editing && (
        <ShiftEditor
          employee={editing.employee}
          date={editing.date}
          dateLabel={`${WEEKDAYS[dates.indexOf(editing.date)]}, ${dayLabel(editing.date)}`}
          shift={editing.shift}
          presets={data?.presets || []}
          onClose={() => setEditing(null)}
          onSave={saveShift}
          onDelete={removeShift}
        />
      )}
    </div>
  );
};

export default ScheduleTab;
